import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';
import { forwardRef, isValidElement } from 'react';

import { useModifiers } from '../../../../styles/hooks/useModifiers';
import { Badge, BadgeSize } from '../../../controls/badge/Badge';
import { Icon as CoreIcon } from '../../../icons/icon';
import { Icons } from '../../../icons/iconNames';

import styles from './gameButton.module.css';

export interface GameButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: keyof Icons | ReactElement;
  active?: boolean;
  border?: boolean;
  pulse?: boolean;
  label?: ReactNode;
  labelClass?: string;
  badge?: string | number | ReactElement;
  grow?: boolean;
  isPassive?: boolean;
}

export const OldGameButton = forwardRef<HTMLDivElement, GameButtonProps>(function GameButtonFunc(
  { icon, active, border, pulse, label, labelClass, badge, grow, className, isPassive, ...rest },
  forwardedRef
) {
  const mods = useModifiers(styles, 'gameButton', { active, border, pulse, grow, isPassive });

  return (
    <button className={`${styles.gameButton} ${mods} ${className ?? ''}`} {...rest}>
      <div className={styles.gameButton__innerWrapper} ref={forwardedRef}>
        <div className={styles.gameButton__border} />
        <div className={styles.gameButton__content}>
          {renderIcon(icon)}
          {label ? <div className={`${styles.gameButton__label} ${labelClass ?? ''}`}>{label}</div> : null}
        </div>
      </div>
      {badge ? <span className={styles.gameButton__badge}>{renderBadge(badge, BadgeSize.Medium)}</span> : null}
    </button>
  );
});

OldGameButton.displayName = 'GameButtonOld';

// Utils

const renderIcon = (icon?: keyof Icons | ReactElement): ReactElement | null => {
  if (isValidElement(icon)) {
    return <div className={styles.gameButton__icon}>{icon}</div>;
  }

  if (typeof icon === 'string') {
    return <CoreIcon icon={icon} className={styles.gameButton__icon} />;
  }

  return null;
};

const renderBadge = (
  badge: string | number | ReactElement | undefined,
  size: BadgeSize.Small | BadgeSize.Medium
): ReactElement | null => {
  if (isValidElement(badge)) {
    return badge;
  }

  if (badge && (typeof badge === 'string' || typeof badge === 'number')) {
    return <Badge size={size} grow="start" value={badge} />;
  }

  return null;
};
